import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { User as UserIcon, Home, FileText, File, Signature, LogOut, Menu, X, Users, Clock, Shield, FolderOpen, Plus, ChevronsLeft, ChevronsRight, LayoutTemplate, Settings, ArrowUpCircle, Moon, Sun } from 'lucide-react';
import { cn } from '../lib/utils';
import { useAuthContext } from '../contexts/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { API_BASE_URL } from '../config/api';
import { Button } from '../components/ui/button';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { usePlan } from '../contexts/PlanContext';
import { useTheme } from '../contexts/ThemeContext';
import WelcomeModal from '../components/WelcomeModal';
import { useTranslation } from 'react-i18next';

// Define the NavigationItem type
interface NavigationItem {
  name: string;
  path: string;
  icon: string;
  disabled?: boolean;
  translationKey: string;
}

const navigationItems: (NavigationItem | 'separator')[] = [
  { name: 'Dashboard', path: '/', icon: 'Home', translationKey: 'nav.dashboard' },
  { name: 'Templates', path: '/templates', icon: 'LayoutTemplate', translationKey: 'nav.templates' },
  { name: 'My Documents', path: '/documents', icon: 'FileText', translationKey: 'nav.my_documents' },
  { name: 'Signing Requests', path: '/signing-requests', icon: 'Signature', translationKey: 'nav.signing_requests' },
  'separator',
  { name: 'Recipients', path: '/recipients', icon: 'Users', translationKey: 'nav.recipients' },
  'separator',
  { name: 'Settings', path: '/settings', icon: 'Settings', translationKey: 'nav.settings' },
];

const adminNavigationItems: NavigationItem[] = [
  { name: 'Users', path: '/admin/users', icon: 'Shield', translationKey: 'nav.admin_users' },
];

const getIcon = (iconName: string) => {
  switch (iconName) {
    case 'Home': return <Home className="h-5 w-5" />;
    case 'FileText': return <FileText className="h-5 w-5" />;
    case 'File': return <File className="h-5 w-5" />;
    case 'Signature': return <Signature className="h-5 w-5" />;
    case 'Users': return <Users className="h-5 w-5" />;
    case 'Shield': return <Shield className="h-5 w-5" />;
    case 'FolderOpen': return <FolderOpen className="h-5 w-5" />;
    case 'LayoutTemplate': return <LayoutTemplate className="h-5 w-5" />;
    case 'Settings': return <Settings className="h-5 w-5" />;
    default: return <Home className="h-5 w-5" />;
  }
};

const getBreadcrumbs = (path: string) => {
  if (path === '/') return [{ translationKey: 'nav.dashboard', path: '/' }];
  
  const parts = path.split('/').filter(Boolean);
  const breadcrumbs = [{ translationKey: 'nav.dashboard', path: '/' }];
  
  let currentPath = '';
  parts.forEach(part => {
    currentPath += `/${part}`;
    const navItem = navigationItems.find((item): item is NavigationItem => item !== 'separator' && item.path === currentPath);
    if (navItem) {
      breadcrumbs.push({ translationKey: navItem.translationKey, path: currentPath });
    } else {
      // Format the part for display (convert kebab-case to Title Case)
      const formattedName = part
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      breadcrumbs.push({ translationKey: formattedName, path: currentPath });
    }
  });
  
  return breadcrumbs;
};

const MainLayout = () => {
  const { user, logout } = useAuthContext();
  const { plan: planData } = usePlan();
  const { theme, toggleTheme } = useTheme();
  const { t } = useTranslation('common');
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('sidebarCollapsed') === 'true');
  const [pendingSigningCount, setPendingSigningCount] = useState(0);
  
  const breadcrumbs = getBreadcrumbs(location.pathname);

  // Show welcome modal for users with incomplete profile (empty first + last name)
  const [showWelcome, setShowWelcome] = useState(() => {
    const stored = localStorage.getItem('user');
    if (!stored) return false;
    try {
      const u = JSON.parse(stored);
      return !u.first_name && !u.last_name;
    } catch { return false; }
  });

  // Categories visible to: all individual customers, or business customer admins
  const showCategories = user?.customer_type === 'individual' || (user?.customer_type === 'business' && user?.is_customer_admin);
  const visibleNavigationItems: (NavigationItem | 'separator')[] = [
    ...navigationItems,
    ...(showCategories ? [{ name: 'Categories', path: '/admin/categories', icon: 'FolderOpen', translationKey: 'nav.admin_categories' } as NavigationItem] : []),
  ];

  // Build avatar API URL
  const getAvatarUrl = () => {
    if (!user?.avatar_url) return undefined;
    return `${API_BASE_URL}/users/${user.id}/avatar`;
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user) return 'VS';
    
    const firstInitial = user.first_name ? user.first_name.charAt(0) : '';
    const lastInitial = user.last_name ? user.last_name.charAt(0) : '';
    
    const initials = (firstInitial + lastInitial).toUpperCase();
    return initials || 'VS';
  };

  // Get user's full name
  const getFullName = () => {
    if (!user) return 'Guest';
    return `${user.first_name} ${user.last_name}`;
  };

  // Get user's current plan name
  const getPlanLabel = () => {
    return planData?.plan?.name || 'Free';
  };

  const showUpgrade = !planData?.plan?.slug || planData.plan.slug === 'free' || planData.plan.slug === 'starter';

  // Get avatar background color based on user role
  const getAvatarBgColor = () => {
    if (!user) return 'bg-gray-500';
    switch (user.role) {
      case 'admin':
        return 'bg-purple-400 text-purple-800';
      case 'requestor':
        return 'bg-blue-400 text-blue-800';
      case 'signatory':
        return 'bg-green-400 text-green-800';
      default:
        return 'bg-gray-400 text-gray-800';
    }
  };


  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };
  
  const handleLogout = async () => {
    try {
      await logout();
      // Force the navigation to happen separately from the async flow
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout failed', error);
    }
  };

  const toggleSidebar = () => {
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
    localStorage.setItem('sidebarCollapsed', String(newState));
  };

  // Fetch pending signing requests count
  const fetchPendingCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const apiUrl = API_BASE_URL.replace(/\/api$/, '');
      const response = await fetch(
        `${apiUrl}/api/signing-requests?status=pending&current_user_only=true&per_page=1`,
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );
      if (response.ok) {
        const data = await response.json();
        setPendingSigningCount(data.total || 0);
      }
    } catch (err) {
      console.error('Error fetching pending signing count:', err);
    }
  }, []);

  useEffect(() => {
    fetchPendingCount();
    const interval = setInterval(fetchPendingCount, 60000);

    // Listen for custom event from child pages (Send, Approve, Decline)
    const handleSigningCountChanged = () => fetchPendingCount();
    window.addEventListener('signing-count-changed', handleSigningCountChanged);

    return () => {
      clearInterval(interval);
      window.removeEventListener('signing-count-changed', handleSigningCountChanged);
    };
  }, [fetchPendingCount]);
  
  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="bg-primary text-primary-foreground h-16 flex items-center px-4 sm:px-6">
        <div className="flex justify-between items-center w-full">
          <div className="flex items-center">
            <Button 
              variant="ghost" 
              size="icon" 
              className="mr-2 md:hidden"
              onClick={toggleMobileMenu}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
            <a href="https://vivosign.com" className="text-xl font-semibold hover:opacity-80 transition-opacity">VivoSign</a>
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-primary-foreground hover:bg-white/20"
              onClick={toggleTheme}
              title={theme === 'dark' ? t('nav.light_mode') : t('nav.dark_mode')}
            >
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <LanguageSwitcher />
            <div className="hidden md:flex items-center gap-3">
              <Link to="/profile" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
                <Avatar className="h-10 w-10 cursor-pointer">
                  {getAvatarUrl() && <AvatarImage src={getAvatarUrl()} alt={getFullName()} />}
                  <AvatarFallback className={`${getAvatarBgColor()} text-sm font-medium`}>
                    {getUserInitials()}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <div className="text-sm font-medium">{getFullName()}</div>
                  <div className="text-xs opacity-90">{getPlanLabel()}</div>
                </div>
              </Link>
              {showUpgrade && (
                <Link to="/settings?tab=billing">
                  <Button size="sm" className="h-7 text-xs bg-amber-500 hover:bg-amber-600 text-white">
                    <ArrowUpCircle className="h-3.5 w-3.5 mr-1" />
                    {t('welcome.upgrade')}
                  </Button>
                </Link>
              )}
            </div>
            <Button 
              variant="ghost" 
              size="icon" 
              className="ml-2"
              onClick={handleLogout}
            >
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>
      
      {/* Main layout with sidebar and content */}
      <div className="flex flex-1 h-[calc(100vh-64px)] overflow-hidden">
        {/* Desktop Sidebar */}
        <div className={cn(
          "hidden md:flex flex-col bg-slate-50 dark:bg-slate-900 border-r dark:border-slate-700 overflow-hidden transition-all duration-200",
          sidebarCollapsed ? "w-16" : "w-64"
        )}>
          {/* New Document Button */}
          <div className={cn("p-3", sidebarCollapsed ? "px-2" : "px-4")}>
            <Link to="/document-editor">
              <Button className={cn(
                "w-full bg-green-600 hover:bg-green-700 text-white",
                sidebarCollapsed && "px-0"
              )}>
                <Plus className={cn("h-4 w-4", !sidebarCollapsed && "mr-2")} />
                {!sidebarCollapsed && t('nav.new_document')}
              </Button>
            </Link>
          </div>

          {/* Separator */}
          <div className={cn("border-t", sidebarCollapsed ? "mx-2" : "mx-4")} />

          <nav className={cn("flex-1 space-y-1 overflow-y-auto pt-2", sidebarCollapsed ? "p-2 pt-2" : "p-4 pt-2")}>
            {visibleNavigationItems.map((item, index) => {
              if (item === 'separator') {
                return <div key={`sep-${index}`} className={cn("border-t my-2", sidebarCollapsed ? "mx-1" : "mx-2")} />;
              }
              const isActive = location.pathname === item.path;
              const icon = getIcon(item.icon);
              const isSigningRequests = item.name === 'Signing Requests';

              if (item.disabled) {
                return (
                  <div
                    key={item.path}
                    className={cn(
                      "relative group flex items-center rounded-md text-sm font-medium text-slate-400 dark:text-slate-500 cursor-not-allowed",
                      sidebarCollapsed ? "justify-center p-2" : "gap-3 px-3 py-2"
                    )}
                  >
                    {icon}
                    {!sidebarCollapsed && (
                      <>
                        <span>{t(item.translationKey)}</span>
                        <Clock className="h-3.5 w-3.5 ml-auto text-slate-300" />
                      </>
                    )}
                    {sidebarCollapsed && (
                      <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 bg-slate-800 dark:bg-slate-600 text-white text-xs rounded px-2 py-1 whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none z-50 transition-opacity">
                        {t(item.translationKey)} ({t('nav.coming_soon')})
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <div key={item.path} className="relative group">
                  <Link
                    to={item.path}
                    className={cn(
                      "flex items-center rounded-md text-sm font-medium transition-colors",
                      sidebarCollapsed ? "justify-center p-2" : "gap-3 px-3 py-2",
                      isActive
                        ? "bg-primary text-primary-foreground"
                        : "text-slate-600 dark:text-slate-300 hover:bg-primary/10 hover:text-primary"
                    )}
                  >
                    <span className="relative shrink-0">
                      {icon}
                      {sidebarCollapsed && isSigningRequests && pendingSigningCount > 0 && (
                        <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[8px] font-bold rounded-full min-w-[14px] h-[14px] flex items-center justify-center px-0.5 leading-none">
                          {pendingSigningCount}
                        </span>
                      )}
                    </span>
                    {!sidebarCollapsed && <span>{t(item.translationKey)}</span>}
                    {!sidebarCollapsed && isSigningRequests && pendingSigningCount > 0 && (
                      <span className="ml-auto bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 leading-none">
                        {pendingSigningCount}
                      </span>
                    )}
                  </Link>
                  {sidebarCollapsed && (
                    <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 bg-slate-800 dark:bg-slate-600 text-white text-xs rounded px-2 py-1 whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none z-50 transition-opacity">
                      {t(item.translationKey)}
                      {isSigningRequests && pendingSigningCount > 0 && ` (${pendingSigningCount})`}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
          {/* Admin section - shown at bottom for admin users */}
          {user?.is_admin && (
            <div className={cn("border-t py-3", sidebarCollapsed ? "px-2" : "px-4")}>
              {!sidebarCollapsed && (
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-2 px-3">{t('nav.administration')}</p>
              )}
              <div className="space-y-1">
                {adminNavigationItems.map((item) => {
                  const isActive = location.pathname === item.path;
                  const icon = getIcon(item.icon);
                  return (
                    <div key={item.path} className="relative group">
                      <Link
                        to={item.path}
                        className={cn(
                          "flex items-center rounded-md text-sm font-medium transition-colors",
                          sidebarCollapsed ? "justify-center p-2" : "gap-3 px-3 py-2",
                          isActive
                            ? "bg-purple-600 text-white"
                            : "text-slate-600 dark:text-slate-300 hover:bg-purple-50 dark:hover:bg-purple-900/30 hover:text-purple-700 dark:hover:text-purple-300"
                        )}
                      >
                        {icon}
                        {!sidebarCollapsed && <span>{t(item.translationKey)}</span>}
                      </Link>
                      {sidebarCollapsed && (
                        <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 bg-slate-800 dark:bg-slate-600 text-white text-xs rounded px-2 py-1 whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none z-50 transition-opacity">
                          {t(item.translationKey)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {/* Collapse toggle + version */}
          <div className={cn("border-t mt-auto", sidebarCollapsed ? "p-2" : "p-4")}>
            <button
              onClick={toggleSidebar}
              className={cn(
                "flex items-center rounded-md text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors w-full",
                sidebarCollapsed ? "justify-center p-2" : "gap-2 px-3 py-2"
              )}
            >
              {sidebarCollapsed ? <ChevronsRight className="h-4 w-4" /> : <ChevronsLeft className="h-4 w-4" />}
              {!sidebarCollapsed && <span className="text-xs font-medium">{t('nav.collapse')}</span>}
            </button>
            {!sidebarCollapsed && (
              <div className="flex items-center gap-2 px-3 mt-2">
                <UserIcon className="h-4 w-4 text-slate-400" />
                <span className="text-xs font-medium text-slate-400 dark:text-slate-500">v1.0.0</span>
              </div>
            )}
          </div>
        </div>
        
        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="absolute inset-0 bg-black/50 z-10 md:hidden" onClick={toggleMobileMenu} />
        )}
        
        <div className={`fixed inset-y-0 left-0 z-20 w-64 bg-white dark:bg-slate-900 transform transition-transform duration-200 ease-in-out md:hidden ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
          <div className="flex flex-col h-full">
            <div className="p-4 border-b">
              <div className="flex items-center justify-between">
                <a href="https://vivosign.com" className="text-lg font-semibold hover:opacity-80 transition-opacity">VivoSign</a>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="mr-2"
                  onClick={toggleMobileMenu}
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>
            
            <nav className="flex-1 space-y-1 p-4 overflow-y-auto">
              {/* New Document Button */}
              <Link to="/document-editor" onClick={toggleMobileMenu}>
                <Button className="w-full bg-green-600 hover:bg-green-700 text-white mb-2">
                  <Plus className="h-4 w-4 mr-2" />
                  {t('nav.new_document')}
                </Button>
              </Link>
              <div className="border-t mb-2" />
              {visibleNavigationItems.map((item, index) => {
                if (item === 'separator') {
                  return <div key={`sep-${index}`} className="border-t my-2" />;
                }
                const isActive = location.pathname === item.path;
                const icon = getIcon(item.icon);

                if (item.disabled) {
                  return (
                    <div
                      key={item.path}
                      className="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-slate-400 dark:text-slate-500 cursor-not-allowed"
                    >
                      {icon}
                      <span>{t(item.translationKey)}</span>
                      <Clock className="h-3.5 w-3.5 ml-auto text-slate-300" />
                    </div>
                  );
                }

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                      isActive
                        ? "bg-primary text-primary-foreground"
                        : "text-slate-600 dark:text-slate-300 hover:bg-primary/10 hover:text-primary"
                    )}
                    onClick={toggleMobileMenu}
                  >
                    {icon}
                    <span>{t(item.translationKey)}</span>
                    {item.name === 'Signing Requests' && pendingSigningCount > 0 && (
                      <span className="ml-auto bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 leading-none">
                        {pendingSigningCount}
                      </span>
                    )}
                  </Link>
                );
              })}
              {/* Admin section for mobile */}
              {user?.is_admin && (
                <div className="border-t mt-4 pt-3">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-2 px-3">{t('nav.administration')}</p>
                  {adminNavigationItems.map((item) => {
                    const isActive = location.pathname === item.path;
                    const icon = getIcon(item.icon);
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={cn(
                          "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                          isActive
                            ? "bg-purple-600 text-white"
                            : "text-slate-600 dark:text-slate-300 hover:bg-purple-50 dark:hover:bg-purple-900/30 hover:text-purple-700 dark:hover:text-purple-300"
                        )}
                        onClick={toggleMobileMenu}
                      >
                        {icon}
                        <span>{t(item.translationKey)}</span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </nav>
            
            <div className="p-4 mt-auto border-t">
              <div className="flex items-center gap-3">
                <Link to="/profile" className="flex items-center gap-3 hover:opacity-80 transition-opacity" onClick={toggleMobileMenu}>
                  <Avatar>
                    {getAvatarUrl() && <AvatarImage src={getAvatarUrl()} alt={getFullName()} />}
                    <AvatarFallback className={`${getAvatarBgColor()} text-sm font-medium`}>
                      {getUserInitials()}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="text-sm font-medium">{getFullName()}</div>
                    <div className="text-xs opacity-90">{getPlanLabel()}</div>
                  </div>
                </Link>
                <div className="flex items-center gap-1 ml-auto">
                  {showUpgrade && (
                    <Link to="/settings?tab=billing" onClick={toggleMobileMenu}>
                      <Button size="sm" className="h-7 text-xs bg-amber-500 hover:bg-amber-600 text-white">
                        <ArrowUpCircle className="h-3.5 w-3.5 mr-1" />
                        {t('welcome.upgrade')}
                      </Button>
                    </Link>
                  )}
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={handleLogout}
                  >
                    <LogOut className="h-5 w-5" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Main content */}
        <div className="flex-1 flex flex-col">
          {/* Breadcrumbs */}
          {breadcrumbs.length > 0 && (
            <div className="px-4 py-2 bg-white dark:bg-slate-900 border-b dark:border-slate-700 sm:px-6">
              <div className="flex items-center text-sm text-muted-foreground">
                {breadcrumbs.map((breadcrumb, index) => (
                  <React.Fragment key={breadcrumb.path}>
                    {index > 0 && <span className="mx-2">/</span>}
                    {index === breadcrumbs.length - 1 ? (
                      <span className="font-medium text-foreground">{t(breadcrumb.translationKey)}</span>
                    ) : (
                      <Link 
                        to={breadcrumb.path} 
                        className="hover:text-foreground"
                      >
                        {t(breadcrumb.translationKey)}
                      </Link>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}
          
          {/* Page content */}
          <main className="flex-1 flex flex-col p-4 sm:p-6 overflow-hidden">
            <Outlet />
          </main>
          <WelcomeModal open={showWelcome} onOpenChange={setShowWelcome} />
        </div>
      </div>
    </div>
  );
};

export default MainLayout;
